import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';
import './FormsEditor.css';

// Worker pad — geserveerd vanuit public/
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

const VELD_TYPES = [
  { value: 'text',       label: 'Tekst (1 regel)' },
  { value: 'textarea',   label: 'Tekst (meerdere regels)' },
  { value: 'number',     label: 'Getal' },
  { value: 'date',       label: 'Datum' },
  { value: 'time',       label: 'Tijd' },
  { value: 'checkbox',   label: 'Checkbox' },
  { value: 'dropdown',   label: 'Keuzelijst' },
  { value: 'radio',      label: 'Radio (1 uit meerdere)' },
  { value: 'signature',  label: 'Handtekening' },
  { value: 'paraaf',     label: 'Paraaf (initialen)' },
  { value: 'meetwaarde', label: 'Meetwaarde (getal + eenheid)' }
];

const RENDER_SCALE = 1.5;        // Browser zoom
const MIN_FIELD_W = 20;
const MIN_FIELD_H = 14;

const FormsEditor = () => {
  const { id: templateId } = useParams();
  const navigate = useNavigate();
  const { gebruiker } = useAuth();

  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);            // [{tempId|id, naam, label, type, page, x_pdf, y_pdf, w_pdf, h_pdf, opties, verplicht, volgorde}]
  const [pages, setPages] = useState([]);              // [{pageNum, width, height, viewport}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);
  const [drawing, setDrawing] = useState(null);        // {pageNum, startX, startY, currentX, currentY}
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showPropsModal, setShowPropsModal] = useState(false);
  const [quickSignMode, setQuickSignMode] = useState(false);

  const canvasRefs = useRef({});                       // { pageNum: canvasElement }
  const overlayRefs = useRef({});                      // { pageNum: overlayElement }

  // ---- Load template + fields + render PDF ---------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tplRes = await api.get(`/form-templates/${templateId}`);
        if (cancelled) return;
        setTemplate(tplRes.data.template);
        const initialFields = (tplRes.data.fields || []).map(f => ({
          id: f.id,
          naam: f.naam,
          label: f.label,
          type: f.type,
          page: f.page,
          x_pdf: parseFloat(f.x),
          y_pdf: parseFloat(f.y),
          w_pdf: parseFloat(f.width),
          h_pdf: parseFloat(f.height),
          verplicht: f.verplicht,
          opties: f.opties,
          volgorde: f.volgorde || 0
        }));
        setFields(initialFields);

        // Load PDF
        const pdfUrl = `/api/form-templates/${templateId}/pdf`;
        const token = localStorage.getItem('token');
        const pdfData = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } });
        if (!pdfData.ok) throw new Error(`PDF laden mislukt (${pdfData.status})`);
        const arrayBuffer = await pdfData.arrayBuffer();

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;

        const pageInfo = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          pageInfo.push({ pageNum: p, width: viewport.width, height: viewport.height, page, viewport });
        }
        if (cancelled) return;
        setPages(pageInfo);

        // Render after state update — done in separate effect
        setLoading(false);
      } catch (err) {
        console.error('Editor laad-fout:', err);
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Fout bij laden template');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [templateId]);

  // ---- Render canvases zodra pages geladen ----------------------------------
  useEffect(() => {
    pages.forEach(({ pageNum, page, viewport }) => {
      const canvas = canvasRefs.current[pageNum];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: ctx, viewport }).promise.catch(e =>
        console.error(`Pagina ${pageNum} render fout:`, e)
      );
    });
  }, [pages]);

  // ---- Coordinate helpers --------------------------------------------------
  // PDF: origin links-ONDER. Browser overlay: origin links-BOVEN.
  const browserToPdf = (px, py, pw, ph, pageHeight) => ({
    x: px / RENDER_SCALE,
    y: (pageHeight - py - ph) / RENDER_SCALE,
    width: pw / RENDER_SCALE,
    height: ph / RENDER_SCALE
  });
  const pdfToBrowser = (xPdf, yPdf, wPdf, hPdf, pageHeight) => ({
    left:   xPdf * RENDER_SCALE,
    top:    pageHeight - (yPdf + hPdf) * RENDER_SCALE,
    width:  wPdf * RENDER_SCALE,
    height: hPdf * RENDER_SCALE
  });

  // ---- Drawing nieuwe velden -----------------------------------------------
  const handleOverlayMouseDown = (pageNum, e) => {
    if (e.target !== e.currentTarget) return;            // klik op bestaand veld
    const rect = e.currentTarget.getBoundingClientRect();
    setDrawing({
      pageNum,
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
      currentX: e.clientX - rect.left,
      currentY: e.clientY - rect.top
    });
    setSelectedFieldKey(null);
  };

  const handleOverlayMouseMove = (e) => {
    if (!drawing) return;
    const overlay = overlayRefs.current[drawing.pageNum];
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    setDrawing(d => ({
      ...d,
      currentX: Math.max(0, Math.min(e.clientX - rect.left, rect.width)),
      currentY: Math.max(0, Math.min(e.clientY - rect.top, rect.height))
    }));
  };

  const handleOverlayMouseUp = () => {
    if (!drawing) return;
    const { pageNum, startX, startY, currentX, currentY } = drawing;
    const w = Math.abs(currentX - startX);
    const h = Math.abs(currentY - startY);

    if (w < MIN_FIELD_W || h < MIN_FIELD_H) {
      setDrawing(null);
      return;
    }

    const x = Math.min(startX, currentX);
    const y = Math.min(startY, currentY);
    const pageInfo = pages.find(p => p.pageNum === pageNum);
    const pdfCoords = browserToPdf(x, y, w, h, pageInfo.height);

    const tempId = `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    let newField;
    if (quickSignMode) {
      const sigCount = fields.filter(f => f.type === 'signature').length + 1;
      newField = {
        tempId,
        naam: `handtekening_${sigCount}`,
        label: `Handtekening ${sigCount}`,
        type: 'signature',
        page: pageNum,
        x_pdf: pdfCoords.x,
        y_pdf: pdfCoords.y,
        w_pdf: pdfCoords.width,
        h_pdf: pdfCoords.height,
        verplicht: true,
        opties: null,
        volgorde: fields.length + 1
      };
    } else {
      newField = {
        tempId,
        naam: `veld_${fields.length + 1}`,
        label: `Veld ${fields.length + 1}`,
        type: 'text',
        page: pageNum,
        x_pdf: pdfCoords.x,
        y_pdf: pdfCoords.y,
        w_pdf: pdfCoords.width,
        h_pdf: pdfCoords.height,
        verplicht: false,
        opties: null,
        volgorde: fields.length + 1
      };
    }

    setFields(f => [...f, newField]);
    setSelectedFieldKey(tempId);
    if (!quickSignMode) setShowPropsModal(true);
    setUnsavedChanges(true);
    setDrawing(null);
  };

  // ---- Veld manipuleren -----------------------------------------------------
  const fieldKey = (f) => f.id ? `id_${f.id}` : f.tempId;

  const updateField = (key, patch) => {
    setFields(arr => arr.map(f => fieldKey(f) === key ? { ...f, ...patch } : f));
    setUnsavedChanges(true);
  };

  const deleteField = (key) => {
    if (!window.confirm('Veld verwijderen?')) return;
    setFields(arr => arr.filter(f => fieldKey(f) !== key));
    if (selectedFieldKey === key) setSelectedFieldKey(null);
    setUnsavedChanges(true);
  };

  // ---- Save -----------------------------------------------------------------
  const handleChangeSigningStrength = async (newStrength) => {
    try {
      await api.put(`/form-templates/${templateId}`, { signing_strength: newStrength });
      setTemplate(t => ({ ...t, signing_strength: newStrength }));
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    }
  };

    const handleSave = async () => {
    // Validatie
    const nameMap = {};
    for (const f of fields) {
      if (!f.naam?.trim()) { alert(`Veld "${f.label || '?'}" mist een naam`); return; }
      if (!f.label?.trim()) { alert(`Veld "${f.naam}" mist een label`); return; }
      if (!/^[a-z0-9_]+$/i.test(f.naam)) {
        alert(`Veld-naam "${f.naam}" mag alleen letters, cijfers en _ bevatten`);
        return;
      }
      if (nameMap[f.naam]) { alert(`Veld-naam "${f.naam}" is dubbel`); return; }
      nameMap[f.naam] = true;
    }

    setSaving(true);
    try {
      const payload = {
        fields: fields.map((f, idx) => ({
          id: f.id || undefined,
          naam: f.naam.trim(),
          label: f.label.trim(),
          type: f.type,
          page: f.page,
          x: f.x_pdf,
          y: f.y_pdf,
          width: f.w_pdf,
          height: f.h_pdf,
          verplicht: !!f.verplicht,
          opties: f.opties || null,
          volgorde: idx + 1,
          rol: f.rol || null
        }))
      };
      const res = await api.post(`/form-templates/${templateId}/fields`, payload);
      // Backend retourneert verse rijen met id's — sync state
      const fresh = (res.data.fields || []).map(f => ({
        id: f.id,
        naam: f.naam,
        label: f.label,
        type: f.type,
        page: f.page,
        x_pdf: parseFloat(f.x),
        y_pdf: parseFloat(f.y),
        w_pdf: parseFloat(f.width),
        h_pdf: parseFloat(f.height),
        verplicht: f.verplicht,
        opties: f.opties,
        volgorde: f.volgorde,
        rol: f.rol
      }));
      setFields(fresh);
      setUnsavedChanges(false);
      alert('✅ Velden opgeslagen');
    } catch (err) {
      console.error('Save fout:', err);
      alert('Fout bij opslaan: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ---- Navigatie waarschuwing ----------------------------------------------
  const handleBack = useCallback(() => {
    if (unsavedChanges && !window.confirm('Onopgeslagen wijzigingen — toch terug?')) return;
    navigate('/forms');
  }, [unsavedChanges, navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unsavedChanges]);

  // ---- Render ---------------------------------------------------------------
  if (loading) return <div className="fe-state">PDF laden…</div>;
  if (error)   return <div className="fe-state fe-error">⚠️ {error}<br/><button className="fb-btn" onClick={() => navigate('/forms')}>Terug</button></div>;

  const selected = selectedFieldKey
    ? fields.find(f => fieldKey(f) === selectedFieldKey)
    : null;

  return (
    <div className="forms-editor">
      <div className="fe-header">
        <button className="fe-btn" onClick={handleBack}>← Terug</button>
        <div className="fe-title">
          <h2>{template?.naam || 'Template'}</h2>
          <small>v{template?.versie_nummer} · {fields.length} velden {unsavedChanges && <span className="fe-unsaved">· niet opgeslagen</span>}</small>
        </div>
        <div className="fe-toolbar">
          <span className="fe-help">
            {quickSignMode ? 'Sleep waar de handtekening moet komen' : 'Sleep een rechthoek om een veld te plaatsen'}
          </span>
          <button
            className={`fe-btn ${quickSignMode ? 'fe-btn-primary' : ''}`}
            onClick={() => setQuickSignMode(m => !m)}
            title="Modus om snel handtekening-velden te plaatsen"
          >
            ✏ Sign here
          </button>
          <select
            value={template?.signing_strength || 'basic'}
            onChange={(e) => handleChangeSigningStrength(e.target.value)}
            style={{ marginRight: 8, padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13, background: template?.signing_strength === 'aes' ? '#fef3c7' : 'white', fontWeight: 600 }}
            title="Beveiligingsniveau voor handtekeningen"
          >
            <option value="basic">🔒 Basic (eIDAS Trede 1)</option>
            <option value="aes">💎 AES Premium (eIDAS Trede 2 + SMS-OTP)</option>
          </select>
          <button
            className="fe-btn fe-btn-primary"
            onClick={handleSave}
            disabled={saving || !unsavedChanges}
          >
            {saving ? 'Opslaan…' : '💾 Opslaan'}
          </button>
        </div>
      </div>

      <div className="fe-body">
        <div className="fe-pdf-area" onMouseMove={handleOverlayMouseMove} onMouseUp={handleOverlayMouseUp}>
          {pages.map(({ pageNum, width, height }) => (
            <div key={pageNum} className="fe-page-wrap">
              <div className="fe-page-label">Pagina {pageNum}</div>
              <div className="fe-page-stage" style={{ width, height }}>
                <canvas
                  ref={el => { canvasRefs.current[pageNum] = el; }}
                  className="fe-canvas"
                  width={width}
                  height={height}
                />
                <div
                  ref={el => { overlayRefs.current[pageNum] = el; }}
                  className="fe-overlay"
                  onMouseDown={(e) => handleOverlayMouseDown(pageNum, e)}
                  style={{ width, height }}
                >
                  {/* Bestaande velden op deze pagina */}
                  {fields.filter(f => f.page === pageNum).map(f => {
                    const pos = pdfToBrowser(f.x_pdf, f.y_pdf, f.w_pdf, f.h_pdf, height);
                    const key = fieldKey(f);
                    const isSelected = selectedFieldKey === key;
                    return (
                      <div
                        key={key}
                        className={`fe-field ${isSelected ? 'fe-field-selected' : ''}`}
                        style={{ left: pos.left, top: pos.top, width: pos.width, height: pos.height }}
                        onMouseDown={(e) => { e.stopPropagation(); setSelectedFieldKey(key); }}
                        onDoubleClick={(e) => { e.stopPropagation(); setSelectedFieldKey(key); setShowPropsModal(true); }}
                        title={`${f.label} (${f.type}) — dubbelklik voor eigenschappen`}
                      >
                        <span className="fe-field-label">{f.label}</span>
                        <span className="fe-field-type">{f.type}</span>
                      </div>
                    );
                  })}

                  {/* Drawing preview */}
                  {drawing && drawing.pageNum === pageNum && (
                    <div
                      className="fe-drawing-preview"
                      style={{
                        left: Math.min(drawing.startX, drawing.currentX),
                        top:  Math.min(drawing.startY, drawing.currentY),
                        width:  Math.abs(drawing.currentX - drawing.startX),
                        height: Math.abs(drawing.currentY - drawing.startY)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sidepanel met velden-lijst */}
        <div className="fe-sidepanel">
          <h3>Velden ({fields.length})</h3>
          {fields.length === 0 && <p className="fe-empty">Nog geen velden — sleep op de PDF om te beginnen.</p>}
          <ul className="fe-fieldlist">
            {fields.map(f => {
              const key = fieldKey(f);
              return (
                <li
                  key={key}
                  className={selectedFieldKey === key ? 'fe-li-selected' : ''}
                  onClick={() => setSelectedFieldKey(key)}
                >
                  <div>
                    <strong>{f.label || '(geen label)'}</strong>
                    <small> · p{f.page} · {f.type}</small>
                    {f.verplicht && <span className="fe-required-tag">verplicht</span>}
                  </div>
                  <button
                    className="fe-icon-btn"
                    onClick={(e) => { e.stopPropagation(); deleteField(key); }}
                    title="Verwijderen"
                  >🗑</button>
                </li>
              );
            })}
          </ul>
          {selected && (
            <button className="fe-btn" onClick={() => setShowPropsModal(true)}>
              ✎ Eigenschappen bewerken
            </button>
          )}
        </div>
      </div>

      {/* Properties modal */}
      {showPropsModal && selected && (
        <FieldPropsModal
          field={selected}
          onSave={(patch) => { updateField(selectedFieldKey, patch); setShowPropsModal(false); }}
          onCancel={() => setShowPropsModal(false)}
          onDelete={() => { deleteField(selectedFieldKey); setShowPropsModal(false); }}
        />
      )}
    </div>
  );
};

// =====================================================
// Properties modal
// =====================================================
const FieldPropsModal = ({ field, onSave, onCancel, onDelete }) => {
  const [naam, setNaam] = useState(field.naam || '');
  const [label, setLabel] = useState(field.label || '');
  const [type, setType] = useState(field.type || 'text');
  const [verplicht, setVerplicht] = useState(!!field.verplicht);
  const [rol, setRol] = useState(field.rol || 'klant');
  const [optiesText, setOptiesText] = useState(
    field.opties?.choices ? field.opties.choices.join('\n') :
    field.opties?.eenheid ? field.opties.eenheid :
    ''
  );

  const heeftKeuzes = type === 'dropdown' || type === 'radio';
  const heeftEenheid = type === 'meetwaarde';
  const heeftRol = type === 'signature' || type === 'paraaf';

  const submit = (e) => {
    e.preventDefault();
    if (!naam.trim() || !label.trim()) {
      alert('Naam en label zijn verplicht');
      return;
    }
    if (!/^[a-z0-9_]+$/i.test(naam.trim())) {
      alert('Naam mag alleen letters, cijfers en _ bevatten (geen spaties)');
      return;
    }

    let opties = null;
    if (heeftKeuzes) {
      const choices = optiesText.split('\n').map(s => s.trim()).filter(Boolean);
      if (choices.length > 0) opties = { choices };
    } else if (heeftEenheid) {
      const eenheid = optiesText.trim();
      if (eenheid) opties = { eenheid };
    }

    onSave({ naam: naam.trim(), label: label.trim(), type, verplicht, opties, rol: heeftRol ? rol : null });
  };

  return (
    <div className="fe-modal-overlay">
      <div className="fe-modal" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <h3>Veld-eigenschappen</h3>
            <button onClick={() => onCancel()} style={{ position: "absolute", top: 12, right: 14, background: "transparent", border: "none", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#94a3b8" }} title="Sluiten">×</button>
        <form onSubmit={submit}>
          <label className="fe-label">
            Naam (machine) <span className="fe-req">*</span>
            <input type="text" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="bv. monteur_naam" maxLength={100} />
            <small>Alleen letters, cijfers, underscore. Wordt gebruikt in API.</small>
          </label>
          <label className="fe-label">
            Label (zichtbaar) <span className="fe-req">*</span>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Naam monteur" maxLength={255} />
          </label>
          <label className="fe-label">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {VELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          {heeftKeuzes && (
            <label className="fe-label">
              Keuzes (één per regel)
              <textarea value={optiesText} onChange={(e) => setOptiesText(e.target.value)} rows={4} placeholder="Optie 1&#10;Optie 2&#10;Optie 3" />
            </label>
          )}
          {heeftEenheid && (
            <label className="fe-label">
              Eenheid
              <input type="text" value={optiesText} onChange={(e) => setOptiesText(e.target.value)} placeholder="bv. mm, V, A, °C" maxLength={20} />
            </label>
          )}
          {heeftRol && (
            <label className="fe-label">
              Rol ondertekenaar
              <select value={rol} onChange={(e) => setRol(e.target.value)}>
                <option value="uitvoerder">Uitvoerder (jij — geen klant-input)</option>
                <option value="klant">Klant (tekent op tablet, met naam + email)</option>
                <option value="adviseur">Adviseur</option>
              </select>
              <small>Klant = gele waarschuwingsbox + naam/email-input. Uitvoerder = directe handtekening.</small>
            </label>
          )}
          <label className="fe-checkbox">
            <input type="checkbox" checked={verplicht} onChange={(e) => setVerplicht(e.target.checked)} />
            Verplicht veld
          </label>
          <div className="fe-modal-acties">
            <button type="button" className="fe-btn fe-btn-danger" onClick={onDelete}>🗑 Verwijderen</button>
            <div className="fe-spacer" />
            <button type="button" className="fe-btn" onClick={onCancel}>Annuleren</button>
            <button type="submit" className="fe-btn fe-btn-primary">Opslaan</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormsEditor;
